import React from 'react';
import { withRouter } from 'react-router-dom';
import { UserInvite } from './UserInvite';
import { userStore } from './UserStore';
import { UsersTable } from './UsersTable';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { Pagination } from 'ats/components/pagination/Pagination';
import { UserEdit } from './UserEdit';
import { ListStore } from '../../shared/stores/ListStore';
import { observer } from 'mobx-react';
import { notificationStore } from '../layout/notifications/NotificationStore';
import { dialogStore } from '../layout/modals/DialogStore';
import { rolesAllowed, USER_ROLES } from '../../shared/security/RolesAllowed';
import SettingsSubsectionHeader from '../settings/SettingsSubsectionHeader';
import SettingsListToolbar from '../settings/SettingsListToolbar';
import SettingsListEmptyState from '../settings/SettingsListEmptyState';
import { getSettingsSectionLabel } from '../settings/settingsSections';

const ALL_FILTER_OPTION = { text: 'All', value: null };

const statusOptions = [
	ALL_FILTER_OPTION,
	{ text: 'Active', value: 'Active' },
	{ text: 'Invited', value: 'Invited' },
	{ text: 'Disabled', value: 'Disabled' }
];

const roleOptions = [
	ALL_FILTER_OPTION,
	{ text: USER_ROLES.ADMIN, value: USER_ROLES.ADMIN },
	{ text: USER_ROLES.MANAGER, value: USER_ROLES.MANAGER },
	{ text: USER_ROLES.RECRUITER, value: USER_ROLES.RECRUITER },
	{ text: USER_ROLES.CANDIDATE, value: USER_ROLES.CANDIDATE },
	{ text: USER_ROLES.ACCOUNT_MANAGER, value: USER_ROLES.ACCOUNT_MANAGER },
	{ text: USER_ROLES.TALENT_LEADER, value: USER_ROLES.TALENT_LEADER }
];

@rolesAllowed([USER_ROLES.ADMIN])
@withRouter
@observer
export class Users extends React.Component {
	constructor(props) {
		super(props);
		this.listStore = new ListStore(userStore, {
			entityName: 'User',
			createModalTitle: 'Invite User',
			createModal: <UserInvite handleSuccess={() => this.listStore.search()} />,
			editModal: item => (
				<UserEdit user={item} handleSuccess={() => this.listStore.search()} />
			)
		});
	}

	componentDidMount() {
		const resetQuery = this.preSetQuery();
		this.listStore.search(resetQuery);
	}

	preSetQuery() {
		this.listStore.store.setQuery('sort', [
			{ column: 'name', asc: true },
			{ column: 'email', asc: true, hideIndicator: true }
		]);
		this.listStore.store.setQuery(
			'pagination',
			this.listStore.store.defaultPagination
		);
		return true;
	}

	hasFilters = () => {
		const { text, status, role } = userStore.query;
		return Boolean((text || '').trim()) || Boolean(status) || Boolean(role);
	};

	clearFilters = () => {
		userStore.setQuery('text', '');
		userStore.setQuery('status', null);
		userStore.setQuery('role', null);
		this.listStore.search();
	};

	render() {
		const isEmpty = !this.listStore.loading && this.listStore.data.length === 0;

		return (
			<div className="Users container">
				<SettingsSubsectionHeader
					title={getSettingsSectionLabel('users')}
					actions={
						<SettingsListToolbar
							field="search"
							autoFocus={true}
							debounce={true}
							debounceTime={400}
							searchPlaceholder="Search"
							searchValue={userStore.query.text || ''}
							onSearchChange={value => {
								userStore.setQuery('text', value);
								this.listStore.search();
							}}
							createLabel={this.listStore.modalsConfigs.createModalTitle}
							onCreate={() => this.listStore.handleCreate()}
							extra={
								<>
									<Dropdown
										value={userStore.query.status ?? null}
										placeholder="Status"
										optionValue="value"
										displayValue={text => `Status: ${text}`}
										onChange={value => {
											userStore.setQuery('status', value);
											this.listStore.search();
										}}
										options={statusOptions}
									/>
									<Dropdown
										value={userStore.query.role ?? null}
										placeholder="Role"
										optionValue="value"
										displayValue={text => `Role: ${text}`}
										onChange={value => {
											userStore.setQuery('role', value);
											this.listStore.search();
										}}
										options={roleOptions}
									/>
								</>
							}
						/>
					}
				/>
				<div className="columns add-margin-top">
					<div className="column is-12 table-wrapper">
						<UsersTable
							data={this.listStore.data}
							loading={this.listStore.loading}
							isEmpty={isEmpty}
							sort={this.listStore.store.query.sort}
							onSort={this.listStore.handleSort}
							onEdit={this.listStore.handleEdit}
							onDisable={async item =>
								await this.listStore.handleDisable(item, true)
							}
							onDelete={this.handleDelete}
							onInvite={user => {
								userStore.resendInvite(user.id).then(result => {
									notificationStore.pushNotification({
										type: notificationStore.SUCCESS_NOTIFICATION,
										title: 'Invite User',
										message: 'The user has been successfully invited.'
									});
									this.listStore.search();
								});
							}}
						/>
						{!this.listStore.loading && isEmpty ? (
							<SettingsListEmptyState
								entityPlural="users"
								hasFilters={this.hasFilters()}
								onClearFilters={this.clearFilters}
							/>
						) : null}
					</div>
				</div>
				<div className="columns">
					<div className="column is-12 bottom-pagination">
						<Pagination
							loading={this.listStore.loading}
							pagination={userStore.query.pagination}
							totalResults={userStore.query.pagination.totalResults}
							onPaginationChange={this.listStore.handlePaginationChange}
						/>
					</div>
				</div>
			</div>
		);
	}

	handleDelete = item => {
		dialogStore.openDialog(
			{
				title: 'Delete User',
				message: `Are you sure you want to delete ${item.name}?`,
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			() => {
				this.listStore.deleteFromDialog(item);
			}
		);
	};
}
